import { useRef, useState } from "react";
import { Camera, FolderOpen, Loader2 } from "lucide-react";

function compressImage(file) {
  return new Promise((resolve) => {
    if (!file.type.startsWith("image/")) {
      const r = new FileReader();
      r.onload = () => resolve(r.result);
      r.readAsDataURL(file);
      return;
    }
    const img = new Image();
    const url = URL.createObjectURL(file);
    img.onload = () => {
      const scale = Math.min(1, 1280 / Math.max(img.width, img.height));
      const c = document.createElement("canvas");
      c.width = Math.round(img.width * scale);
      c.height = Math.round(img.height * scale);
      c.getContext("2d").drawImage(img, 0, 0, c.width, c.height);
      URL.revokeObjectURL(url);
      resolve(c.toDataURL("image/jpeg", 0.72));
    };
    img.onerror = () => {
      const r = new FileReader();
      r.onload = () => resolve(r.result);
      r.readAsDataURL(file);
    };
    img.src = url;
  });
}

export function CameraButtons({ onCapture, compact = false, cameraLabel = "Foto aufnehmen", galleryLabel = "Aus Galerie" }) {
  const camRef = useRef(null);
  const galRef = useRef(null);
  const [busy, setBusy] = useState(false);

  const handle = async (e) => {
    const files = Array.from(e.target.files || []);
    e.target.value = "";
    if (!files.length) return;
    setBusy(true);
    const out = [];
    for (const f of files) out.push(await compressImage(f));
    setBusy(false);
    onCapture(out);
  };

  const base = compact
    ? "flex items-center gap-1.5 text-xs font-medium px-3 py-2.5 rounded-lg border transition-colors min-h-[44px]"
    : "flex-1 flex items-center justify-center gap-2 text-sm font-medium px-4 py-3.5 rounded-xl border transition-colors min-h-[48px]";

  return (
    <div className={compact ? "flex gap-2" : "flex gap-3"} data-testid="camera-buttons">
      <button type="button" onClick={() => camRef.current?.click()} disabled={busy}
        data-testid="camera-capture-button"
        className={`${base} bg-zinc-900 text-white border-zinc-900 hover:bg-zinc-800 active:scale-[0.98]`}>
        {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Camera className="w-4 h-4" />} {cameraLabel}
      </button>
      <button type="button" onClick={() => galRef.current?.click()} disabled={busy}
        data-testid="gallery-pick-button"
        className={`${base} bg-white text-zinc-700 border-zinc-300 hover:border-zinc-500 active:scale-[0.98]`}>
        <FolderOpen className="w-4 h-4" /> {galleryLabel}
      </button>
      <input ref={camRef} type="file" accept="image/*" capture="environment" className="hidden" onChange={handle} data-testid="camera-capture-input" />
      <input ref={galRef} type="file" accept="image/*" multiple className="hidden" onChange={handle} data-testid="gallery-pick-input" />
    </div>
  );
}
